import React from 'react'
import { Box, Grid, Typography, Skeleton } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import clsx from 'clsx'
import ContentTypeBadge from '../../content-type-badge'
import { contentUrl } from '../../../helpers/url'
import Link from '../../link'

export const useStyles = makeStyles(
  ({ palette, spacing, typography, breakpoints }) => ({
    category: {
      textTransform: 'capitalize',
      '& p': {
        fontSize: '0.75rem !important',
        marginLeft: spacing(0.5)
      },
      '& svg': {
        fontSize: '0.75rem !imporant'
      }
    },
    title: {
      ...typography.body.s,
      fontWeight: typography.weight.medium
    },
    subtitle: {
      ...typography.body.s,
      color: '#454545'
    },
    // Long subtitles ran to five lines and stretched the card; `subtitleLines`
    // caps them with an ellipsis.
    clampedSubtitle: ({ subtitleLines }) => subtitleLines
      ? {
          display: '-webkit-box',
          WebkitLineClamp: subtitleLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }
      : {},
    // PENG-3630: beside a thumbnail the type leads as an eyebrow, 5px over
    // the title, with the subtitle 3px under it (Fibonacci, so the three read
    // as one group), one line each so they fit the image's 72px. The gaps are
    // between the letters: the title and subtitle lines carry 3px of leading
    // above and below, and the type's icon sits 2px below its label.
    compact: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0
    },
    eyebrow: {
      '& .MuiTypography-root': {
        lineHeight: 1
      }
    },
    compactSubtitle: {
      marginTop: -3
    },
    oneLine: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  })
)

const ArticleSummary = props => {
  const classes = useStyles(props)

  const {
    article: { friendlyUrl, title, subtitle, primaryType } = {},
    className = '',
    compact = false
  } = props

  const CardType = () => {
    if (!primaryType || !primaryType.length) {
      return <Skeleton width='20%' />
    }

    return (
      <ContentTypeBadge
        primaryType={primaryType}
        dataTest={`article-card-type-${friendlyUrl}`}
      />
    )
  }

  if (compact) {
    return (
      <Box className={clsx(classes.compact, className)}>
        <Typography
          variant='caption'
          color='textSecondary'
          component='div'
          className={clsx(classes.category, classes.eyebrow)}
          data-test={`article-card-type-${friendlyUrl}`}
        >
          <CardType />
        </Typography>
        <Typography
          variant='body1'
          component='h3'
          className={clsx(classes.title, classes.oneLine)}
          data-test={`article-card-title-${friendlyUrl}`}
        >
          <Link to={title ? contentUrl(props.article) : ''}>
            {title ?? <Skeleton width='80%' />}
          </Link>
        </Typography>
        <Typography
          variant='body2'
          color='textSecondary'
          className={clsx(classes.subtitle, classes.oneLine, classes.compactSubtitle)}
          data-test={`article-card-subtitle-${friendlyUrl}`}
        >
          {subtitle?.html ?? <Skeleton width='100%' />}
        </Typography>
      </Box>
    )
  }

  return (
    /* eslint-disable multiline-ternary */
    <Grid container direction='column' spacing={1} className={className}>
      <Grid item>
        <Typography
          variant='body1'
          component='h3'
          className={classes.title}
          data-test={`article-card-title-${friendlyUrl}`}
        >
          <Link to={title ? contentUrl(props.article) : ''}>
            {title ?? <Skeleton width='80%' />}
          </Link>
        </Typography>
      </Grid>
      <Grid item>
        <Typography
          variant='body2'
          color='textSecondary'
          className={clsx(classes.subtitle, classes.clampedSubtitle)}
          data-test={`article-card-subtitle-${friendlyUrl}`}
        >
          {subtitle?.html ?? (
            <>
              <Skeleton width='100%' />
              <Skeleton width='100%' />
            </>
          )}
        </Typography>
      </Grid>
      {/* No extra margin under the type: the card's own 16px padding is the
          bottom gap, matching its sides (it used to be 32px). */}
      <Grid item>
        <Typography
          variant='caption'
          color='textSecondary'
          className={classes.category}
          data-test={`article-card-type-${friendlyUrl}`}
        >
          <CardType />
        </Typography>
      </Grid>
    </Grid>
  )
}

export default ArticleSummary
